import {MatDialog} from '@angular/material/dialog';
import {
    PreviewImageDialogComponent
} from '@emm/common-items/dialogs/preview-image-dialog/preview-image-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {ItemDataService} from '../../services/item-data.service';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {ItemDetailStoreService} from '@emm/menu/store/item-detail/item-detail-store.service';
import {catchError, filter, takeUntil, tap} from 'rxjs/operators';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {Variation} from '@emm/common-items/models/variation.model';
import {ComponentDetails} from '@emm/common-items/models/component.model';
import {environment} from '@env';
import {formatDateToMidnight} from '@emm/core/util/menu-date.util';
import {
    AttributeOverrideDetailDialogComponent
} from '@emm/common-items/dialogs/attribute-override-detail-dialog/attribute-override-detail-dialog.component';
import {ItemValueOverride} from '@emm/common-items/components/value-overrides-card/value-overrides-card.component';
import {EffectiveDateService} from '@emm/common-items/services/effective-date.service';

@Component({
    selector: 'emm-common-item-details',
    templateUrl: './common-item-details.component.html',
    styleUrls: ['./common-item-details.component.scss'],
    standalone: false
})
export class CommonItemDetailsComponent implements OnInit, OnChanges, OnDestroy {

    protected readonly environment = environment;
    @Input() selectedItem: any;
    @Input() isReadOnly = true;
    @Input() useStoreData = false;
    @Input() context: {
        featureScope: string;
        location: string;
        submenu: string;
        consumer?: string;
    };
    baseVariation: Variation = {
        itemTag: 'BASE_ITEM',
        variationTag: 'BASE_ITEM',
        variationLabel: 'Base Item',
        variationDescription: 'Base Item',
        locationId: 'CHAIN',
        overrides: {}
    };
    variation$ = new BehaviorSubject<Variation>(this.baseVariation);
    variations$ = new BehaviorSubject<Variation[]>([]);

    components$ = new BehaviorSubject<ComponentDetails[] | []>([]);

    form: FormGroup;
    formReady = false;
    item$ = new BehaviorSubject<any>(null);
    showCommonItems$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS);
    effectiveDate: Date | null = null;
    minDate: Date;
    locale: string | null = 'en-US';
    private destroy$ = new Subject<void>();
    isLoading$ = new BehaviorSubject<boolean>(true);

    attributesByGroup: Record<string, ItemAttribute[] | any> = {};
    attributeGroups: string[] = [];

    constructor(
        private dialog: MatDialog,
        private itemDataService: ItemDataService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private launchDarklyService: LaunchDarklyService,
        private itemDetailStore: ItemDetailStoreService,
        private notificationService: NotificationService,
        private router: Router,
        private effectiveDateService: EffectiveDateService,
    ) {
        this.effectiveDate = this.effectiveDateService.effectiveDate;
        this.minDate = this.effectiveDateService.minDate;
    }

    ngOnInit() {
        if (this.useStoreData) {
            this.isReadOnly = true;
            this.itemDetailStore.queries.selected$.pipe(
                takeUntil(this.destroy$),
                filter(item => !!item),
                tap(item => {
                    this.item$.next(item);
                    this.buildDynamicForm();
                    this.variations$.value.push(this.baseVariation);
                    this.changeVariation(this.baseVariation);
                })
            ).subscribe();
        } else {
            this.loadItem();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('selectedItem' in changes && !changes['selectedItem'].firstChange) {
            const currentItem = changes['selectedItem'].currentValue;
            const previousItem = changes['selectedItem'].previousValue;

            if (this.item$.value?.tag === currentItem?.tag) {
                return;
            }

            if (currentItem?.class === 'ITEM_GROUPING' && currentItem?.tag !== previousItem?.tag) {
                this.handleItemChange(currentItem.tag);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleItemChange(newItemTag: string) {
        this.isLoading$.next(true);
        this.resetComponentState();
        this.loadItemByTag(newItemTag);
    }

    resetComponentState() {
        this.formReady = false;
        this.variation$.next(this.baseVariation);
        this.variations$.next([]);
        this.components$.next([]);
        this.attributesByGroup = {};
        this.attributeGroups = [];
    }

    private getItemServiceCall(tag: string, effectiveDate?: string): Observable<any> {
        if (this.isModifier) {
            return this.itemDataService.getModifier(tag, effectiveDate);
        } else if (this.isModifierGrouping) {
            return this.itemDataService.getModifierGroup(tag, effectiveDate);
        } else if (this.isItemGrouping) {
            return this.itemDataService.getItemGroup(tag, effectiveDate);
        } else {
            return this.itemDataService.getItem(tag, effectiveDate || formatDateToMidnight(this.effectiveDate.toISOString()));
        }
    }

    loadItemByTag(tag: string) {
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());

        this.getItemServiceCall(tag, effectiveDate).pipe(
            tap((value) => {
                if (value) {
                    this.item$.next(value);
                    this.buildDynamicForm();
                } else {
                    this.notificationService.error('Item not found');
                    this.isLoading$.next(false);
                }
            }),
            catchError(error => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.isLoading$.next(false);
                return of(null);
            })
        ).subscribe();
    }

    loadItem() {
        const item = this.selectedItem;
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        if (!item) {
            const id = this.route.snapshot.paramMap.get('id');
            this.getItemServiceCall(id, effectiveDate).pipe(
                tap(value => {
                    if (value) {
                        this.item$.next(value);
                    }
                    this.buildDynamicForm();
                })
            ).subscribe();
        } else if (item) {
            this.item$.next(item);
            this.buildDynamicForm();
        } else {
            this.attributesByGroup = {};
            this.attributeGroups = [];
        }
    }

    get itemClass(): string {
        const item = this.currentItem;
        return item?.class || '';
    }

    get itemObjectType(): string {
        const item = this.currentItem;
        return item?.objectType || '';
    }

    get isItem(): boolean {
        const isItemClass = this.itemClass === 'ITEM' || this.itemObjectType === 'ITEM';
        return isItemClass || this.router.url.includes('item-detail');
    }

    get isItemGrouping(): boolean {
        const isItemGroupClass = this.itemClass === 'ITEM_GROUPING' || this.itemObjectType === 'ITEM_GROUPING';
        return isItemGroupClass || this.router.url.includes('item-group-detail');
    }

    get isModifier(): boolean {
        const isModifierClass = this.itemClass === 'MODIFIER' || this.itemObjectType === 'MODIFIER';
        return isModifierClass || this.router.url.includes('modifier-detail');
    }

    get isModifierGrouping(): boolean {
        const isModifierGroupClass = this.itemClass === 'MODIFIER_GROUPING' || this.itemObjectType === 'MODIFIER_GROUPING';
        return isModifierGroupClass || this.router.url.includes('modifier-group-detail');
    }

    get itemClassLabel(): string {
        switch (this.itemClass) {
            case 'ITEM':
                return 'Item';
            case 'ITEM_GROUPING':
                return 'Item Group';
            case 'MODIFIER':
                return 'Modifier';
            case 'MODIFIER_GROUPING':
                return 'Modifier Group';
            default:
                return '';
        }
    }

    get componentCardLabel(): string {
        if (this.isItem) return 'Modifier Group';
        if (this.isItemGrouping) return 'Item';
        if (this.isModifierGrouping) return 'Modifier';
        return '';
    }

    getNewAttributeGroup(attribute: ItemAttribute): string {
        let keyAttributes: string[] = [];
        let groupName = 'Item';

        if (this.isItem) {
            keyAttributes = ['tag', 'name', 'description', 'retailModifiedItemId', 'subsGroupId', 'disabled'];
            groupName = 'Item';
        } else if (this.isItemGrouping) {
            keyAttributes = ['tag', 'name', 'description', 'defaultTag', 'disabled'];
            groupName = 'Item Group';
        } else if (this.isModifier) {
            keyAttributes = ['tag', 'name', 'description', 'retailModifiedItemId', 'subsGroupId', 'modifierType', 'disabled'];
            groupName = 'Modifier';
        } else if (this.isModifierGrouping) {
            keyAttributes = ['tag', 'name', 'description', 'modifierTypeGrouping', 'maximum', 'disabled'];
            groupName = 'Modifier Group';
        }

        const isKeyAttribute = keyAttributes.includes(attribute.jsonProperty);
        if (isKeyAttribute) {
            return groupName;
        } else {
            const groupMapping: Record<string, string> = {
                CORE: 'Others',
                PRICING: 'Pricing',
                IMAGE: 'Images',
                UI_VISIBILITY: 'Others',
            };
            const oldGroup = attribute.attributeGroup;
            return groupMapping[oldGroup] || 'Others';
        }
    }

    private sortAttributesByPriority(attrs: ItemAttribute[]): ItemAttribute[] {
        if (!Array.isArray(attrs) || !attrs.length) {
            return attrs;
        }

        const sortKey = (attr: ItemAttribute) => (attr?.name || attr?.jsonProperty || '').toString().toLowerCase();
        const sorted = [...attrs].sort((a, b) => {
            const aReq = !!a.required;
            const bReq = !!b.required;
            if (aReq !== bReq) {
                return aReq ? -1 : 1;
            }
            return sortKey(a).localeCompare(sortKey(b));
        });

        const priorityOrder = ['name'];
        const lower = (v: string) => v?.toLowerCase();
        const isPriority = (attr: ItemAttribute) => {
            const key = lower(attr.jsonProperty || attr.name);
            return priorityOrder.includes(key);
        };
        const priority = priorityOrder.flatMap(p => sorted.filter(a => lower(a.jsonProperty || a.name) === p));

        if (!priority.length) {
            return sorted;
        }

        const rest = sorted.filter(a => !isPriority(a));
        return [...priority, ...rest];
    }

    private elevateNameAndTag(attrs: ItemAttribute[]): ItemAttribute[] {
        return this.sortAttributesByPriority(attrs);
    }

    transformAttributes(item) {
        if (!item) {
            this.attributesByGroup = {};
            this.attributeGroups = [];
            return;
        }

        this.attributesByGroup = {};
        this.attributeGroups = [];
        const attrs = item.attributes;
        if (attrs && Array.isArray(attrs) && attrs.length > 0) {
            attrs.forEach((attr: ItemAttribute) => {
                const newGroup = this.getNewAttributeGroup(attr);
                if (!this.attributesByGroup[newGroup]) {
                    this.attributesByGroup[newGroup] = [];
                    if (!this.attributeGroups.includes(newGroup)) {
                        this.attributeGroups.push(newGroup);
                    }
                }
                this.attributesByGroup[newGroup].push(attr);
            });

            Object.keys(this.attributesByGroup).forEach(group => {
                this.attributesByGroup[group] = this.sortAttributesByPriority(this.attributesByGroup[group]);
            });

            const groupOrder = [this.itemClassLabel, 'Pricing', 'Images', 'Others'];
            this.attributeGroups.sort((a, b) => {
                const indexA = groupOrder.indexOf(a);
                const indexB = groupOrder.indexOf(b);
                return indexA - indexB;
            });
        } else {
            const defaultGroup = this.itemClassLabel || 'Item';
            this.attributesByGroup[defaultGroup] = [];
            this.attributeGroups.push(defaultGroup);

            Object.keys(item).forEach(key => {
                if (this.shouldIncludeProperty(key)) {
                    const pseudoAttribute = {
                        jsonProperty: key,
                        value: item[key],
                        name: this.capitalizeWord(key),
                        required: key === 'name'
                    } as any;
                    this.attributesByGroup[defaultGroup].push(pseudoAttribute);
                }
            });
            this.attributesByGroup[defaultGroup] = this.sortAttributesByPriority(this.attributesByGroup[defaultGroup]);
        }
    }

    capitalizeWord(value: string): string {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    private deriveComponents(item: any, componentsSource?: any[]): any[] {
        const componentsValue = componentsSource ?? item.components ??
            item.attributes?.find(attr => attr.jsonProperty === 'components')?.value;
        const derivedComponents = (!componentsValue ||
            (Array.isArray(componentsValue) && componentsValue.length === 0)) &&
        item?.componentDetailsMap ? Object.keys(item.componentDetailsMap) : componentsValue;
        return derivedComponents || [];
    }

    private createFormControlsFromAttributes(attributes: ItemAttribute[]): Record<string, FormControl> {
        const formControls: Record<string, FormControl> = {};
        const usAttributes = attributes.filter(attr => attr.countryCode === 'US');
        const ordered = this.elevateNameAndTag(usAttributes);

        ordered.forEach((attr: ItemAttribute) => {
            let value = attr.value;
            if (attr.jsonProperty === 'disabled') {
                value = value === true || value === 'true';
            }

            formControls[attr.jsonProperty] = new FormControl({
                value,
                disabled: true
            });
        });

        return formControls;
    }

    private createFormControlsFromItem(item: any): Record<string, FormControl> {
        const formControls: Record<string, FormControl> = {};

        Object.keys(item).forEach(key => {
            if (this.shouldIncludeProperty(key)) {
                let value = (item as any)[key];
                if (key === 'disabled') {
                    value = value === true || value === 'true';
                }

                formControls[key] = new FormControl({
                    value,
                    disabled: true
                });
            }
        });

        return formControls;
    }

    buildDynamicForm() {
        this.formReady = false;
        const item = this.item$.value;
        this.components$.next(this.deriveComponents(item));

        const sanitizedAttributes = (item?.attributes || []).filter(attr => {
            const key = attr.jsonProperty?.trim();
            return key !== 'components' && key !== 'componentDetailsMap';
        });

        this.transformAttributes({...item, attributes: sanitizedAttributes});

        let formControls: Record<string, FormControl> = {};
        if (sanitizedAttributes.length) {
            formControls = this.createFormControlsFromAttributes(sanitizedAttributes);
        } else if (item) {
            formControls = this.createFormControlsFromItem(item);
        }

        this.form = this.fb.group(formControls);
        this.formReady = true;
        this.isLoading$.next(false);
    }

    shouldIncludeProperty(propertyName: string): boolean {
        const excludedProperties = ['id', 'tag', 'class', 'type', 'migrated', 'components', 'componentDetails',
            'componentDetailsMap', 'attributes', 'createdDate', 'updatedDate', 'createdBy', 'updatedBy', 'objectType'];
        return !excludedProperties.includes(propertyName);
    }

    getAttributesByGroup(group: string): ItemAttribute[] {
        return this.attributesByGroup[group] || [];
    }

    getFormControlType(attribute: ItemAttribute): string {
        if (attribute?.jsonProperty === 'disabled') {
            return 'checkbox';
        }

        switch (attribute?.type) {
            case 'TEXT':
                return 'input';
            case 'TEXTAREA':
                return 'textarea';
            case 'NUM':
                return 'number';
            case 'BOOL':
                return 'checkbox';
            case 'SELECT':
                return 'select';
            case 'MULTI_SELECT':
                return 'multi-select';
            case 'IMAGE':
                return 'input';
            default:
                return 'input';
        }
    }

    getAttributeOptions(attribute: ItemAttribute): any[] {
        if (!attribute?.options) {
            return [];
        }

        const options = attribute.options as any;

        if (Array.isArray(options)) {
            return options;
        }

        if (typeof options === 'string') {
            return options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);
        }

        if (typeof options === 'object') {
            return [options];
        }

        return [];
    }

    get currentItem() {
        return this.item$.value;
    }

    get itemName(): string {
        return this.currentItem?.name || '';
    }

    get itemTag(): string {
        return this.currentItem?.tag || '';
    }

    get itemAssignmentType(): string {
        return this.currentItem?.assignmentType || '';
    }

    get itemTypeName(): string {
        return this.currentItem?.typeName || this.currentItem?.type || '';
    }

    get itemDescription(): string {
        const item = this.currentItem;
        if (!item?.attributes) return '';
        const descAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'description');
        return descAttr?.value || '';
    }

    previewImage(attribute): void {
        if (attribute.value) {
            this.dialog.open(PreviewImageDialogComponent, {
                data: {imageUrl: attribute.value},
                width: '500px',
                maxWidth: '90vw',
                panelClass: 'modifier-image-dialog-panel'
            });
        } else {
            this.notificationService.error('No image URL was found');
        }
    }

    getDisplayImage(): string {
        const item = this.currentItem;
        if (!item) return null;

        if (this.isModifier || this.isModifierGrouping) {
            return item?.image;
        }

        if (!item?.attributes) return null;
        const mobileImageAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'mobileImage');
        const webImageAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'webImage');
        return mobileImageAttr?.value || webImageAttr?.value || null;
    }

    changeVariation(variation: Variation) {
        const filteredVariation = {
            ...variation,
            overrides: Object.keys(variation.overrides || {})
                .filter(key => key !== 'components')
                .reduce((filtered, key) => {
                    filtered[key] = variation.overrides[key];
                    return filtered;
                }, {})
        };
        this.variation$.next(filteredVariation);

        const currentVariation: Variation = this.variations$.value.find(variant => variant.variationTag === variation.variationTag);
        if (this.variation$.value.variationLabel !== 'Base Item') {
            this.resetFormToBaseValues();
            this.form.disable();
            const overrides = currentVariation?.overrides || {};
            Object.entries(overrides).forEach(([key, value]) => {
                if (key !== 'components') {
                    if (this.form.controls[key]) {
                        this.form.controls[key].setValue(value);
                    }
                }
            });
            if (this.useStoreData) {
                if (currentVariation.overrides?.hasOwnProperty('components')) {
                    this.components$.next(currentVariation.overrides.components || []);
                } else {
                    const attrComponents = this.item$.value.attributes?.find(attr => attr.jsonProperty === 'components')?.value;
                    const fallback = (!attrComponents || (Array.isArray(attrComponents) && attrComponents.length === 0)) &&
                    this.item$.value?.componentDetailsMap ? Object.keys(this.item$.value.componentDetailsMap) : attrComponents;
                    this.components$.next(fallback || []);
                }
            }
        } else {
            this.resetToBaseItem();
        }
    }

    private resetFormToBaseValues(): void {
        const item = this.item$.value;
        if (!item) return;

        if (item.attributes) {
            const attributes = this.useStoreData ? item.attributes :
                item.attributes.filter(attr => attr.countryCode === 'US');
            attributes.forEach((attr: ItemAttribute) => {
                if (this.form.controls[attr.jsonProperty]) {
                    let value = attr.value;
                    if (attr.jsonProperty === 'disabled') {
                        value = value === true || value === 'true';
                    }
                    this.form.controls[attr.jsonProperty].setValue(value, {emitEvent: false});
                }
            });
        } else {
            Object.keys(item).forEach(key => {
                if (this.form.controls[key] && this.shouldIncludeProperty(key)) {
                    let value = item[key];
                    if (key === 'disabled') {
                        value = value === true || value === 'true';
                    }
                    this.form.controls[key].setValue(value, {emitEvent: false});
                }
            });
        }
    }

    resetToBaseItem() {
        const item = this.item$.value;
        this.transformAttributes(item);

        let formControls: Record<string, FormControl> = {};
        if (item?.attributes) {
            const attributes = this.useStoreData ? item?.attributes :
                item?.attributes?.filter(attr => attr.countryCode === 'US');
            const ordered = this.elevateNameAndTag(attributes);
            ordered?.forEach((attr: ItemAttribute) => {
                formControls[attr.jsonProperty] = new FormControl({
                    value: attr.value,
                    disabled: true
                });
            });
        } else if (item) {
            formControls = this.createFormControlsFromItem(item);
        }

        this.form = this.fb.group(formControls);
        this.form.disable({emitEvent: false});

        const baseComponents = this.useStoreData ?
            this.item$.value.attributes?.find(attr => attr.jsonProperty === 'components')?.value :
            this.item$.value.components;
        this.components$.next(this.deriveComponents(item, baseComponents));
    }

    copyTagToClipboard(): void {
        navigator.clipboard.writeText(this.itemTag).then(() => {
            this.notificationService.success(`${this.itemTag} copied to clipboard`);
        }).catch(err => {
            this.notificationService.error(`Failed to copy tag: ${err}`);
        });
    }


    getOverridesForAttribute(attribute: ItemAttribute): ItemValueOverride[] {
        const item = this.item$.value;
        if (!item?.itemValueOverrides?.length) {
            return [];
        }
        return item.itemValueOverrides.filter(
            (o: ItemValueOverride) => o.attribute === attribute.jsonProperty && !o.base
        );
    }

    hasVariationOverride(attribute: ItemAttribute): boolean {
        return this.getOverridesForAttribute(attribute).length > 0;
    }

    openAttributeOverrideDialog(attribute: ItemAttribute): void {
        const overrides = this.getOverridesForAttribute(attribute);
        const formValue = attribute.value;
        const baseValue = overrides.find(o => o.baseValue != null)?.baseValue ?? undefined;
        this.dialog.open(AttributeOverrideDetailDialogComponent, {
            width: 'auto',
            maxWidth: '80vw',
            maxHeight: '80vh',
            data: {
                attributeName: attribute.name || attribute.jsonProperty,
                attributeJsonProperty: attribute.jsonProperty,
                overrides,
                formValue,
                baseValue,
                itemTag: this.itemTag,
                itemClass: this.itemClass || this.itemObjectType,
            }
        });
    }
}
